/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles } from '@material-ui/core';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import i18n from 'i18n';
import {
  deleteLicenseGroupRequest,
  getLicenseGroupsRequest,
} from 'modules/environments/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import Permission from 'components/ValidPermission';
import AddIcon from '@material-ui/icons/Add';
import Button from 'components/Button';
import { useParams } from 'react-router';
import {
  LicenseGroupsFields,
  ListLicenseGroupsProps,
} from 'modules/environments/interfaces';
import RefreshButton from 'components/RefreshButton';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import { dateFormatWithAt } from 'utils/helpers';
import styles, { overrideModal } from './styles';
import TitleBehavior from '../FormBehavior/TitleBehavior';
import FormLicenseGroups from './FormLicenseGroups';
import DetailsLicenseGroup from './DetailsLicenseGroup';
import EditClients from './EditClients';

const useStyles = makeStyles(styles);

interface Props {
  listLicenseGroups: ListLicenseGroupsProps;
  isLoadingLicenseGroups: boolean;
  getLicenseGroupsRequest: (props) => void;
  deleteLicenseGroupRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
}

const LicenseGroups = ({
  listLicenseGroups,
  isLoadingLicenseGroups,
  getLicenseGroupsRequest,
  deleteLicenseGroupRequest,
  modalClose,
  modalOpen,
}: Props) => {
  const classes = useStyles();

  const {
    query,
    searchValue,
    itemOrder,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters();
  const [refresh, setRefresh] = React.useState(false);
  const { id, uuid } = useParams<{ id: string; uuid: string }>();

  const licenseGroupUuidRedirected = uuid;

  const getLicenseGroup = (refresh = false) => {
    getLicenseGroupsRequest({
      environmentUuid: id,
      query,
    });

    setRefresh(refresh);
  };

  const headers = [
    {
      key: 'name',
      i18n: 'GROUP_KEY',
      order: true,
    },
    {
      key: 'license_limit',
      i18n: 'BULK_CLIENTS.LICENSES_QUANTITY_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'clients_count',
      i18n: 'APPLICATIONS_CLIENTS_COUNT_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'created_at',
      i18n: 'CREATED_AT_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'updated_at',
      i18n: 'UPDATED_AT_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: '',
      i18n: 'ACTIONS_KEY',
      centerTitle: true,
    },
  ];

  React.useEffect(() => {
    getLicenseGroup();
  }, [getLicenseGroupsRequest, query]);

  const handleCreateGroup = () => {
    modalOpen({
      title: i18n.t('CREATE_LICENSE_GROUP_KEY'),
      content: (
        <FormLicenseGroups
          isCreate
          queryGroups={query}
          licenseLimitAvailable={
            listLicenseGroups?.available_licenses_new_group
          }
        />
      ),
    });
  };

  const handleEditGroup = (item: LicenseGroupsFields) => {
    modalOpen({
      title: i18n.t('UPDATE_LICENSE_GROUP_KEY'),
      content: (
        <FormLicenseGroups
          queryGroups={query}
          initialValues={item}
          licenseLimitAvailable={item?.available_licenses_to_group}
        />
      ),
    });
  };

  const handleDetailsGroup = (uuid: string) => {
    modalOpen({
      title: i18n.t('GROUP_DETAILS_KEY'),
      content: <DetailsLicenseGroup queryGroups={query} uuid={uuid} />,
      genericFooter: {},
    });
  };

  React.useEffect(() => {
    if (licenseGroupUuidRedirected) {
      handleDetailsGroup(licenseGroupUuidRedirected);
    }
  }, [licenseGroupUuidRedirected]);

  const handleEditClientsModal = (item: LicenseGroupsFields) => {
    modalOpen({
      title: i18n.t('EDIT_CLIENTS_IN_GROUP_KEY'),
      maxWidth: 'md',
      overrideClasses: overrideModal,
      content: <EditClients queryGroups={query} initialValues={item} />,
    });
  };

  const handleDeleteGroup = (uuid: string) => {
    modalOpen({
      title: i18n.t('DELETE_LICENSE_GROUP_KEY'),
      content: i18n.t('DELETE_LICENSE_GROUP_CONFIRM_KEY'),
      footer: (
        <Grid>
          <Button
            id="delete-group"
            submitButton
            onClick={() => {
              deleteLicenseGroupRequest({
                environmentUuid: id,
                licenseGroupUuid: uuid,
                query,
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  return (
    <>
      <TitleBehavior
        title={i18n.t('LICENSE_GROUP_KEY')}
        subtitle={i18n.t('LICENSE_GROUP_SUBTITLE_KEY')}
      />
      <Grid container alignItems="center" className={classes.actionsHeader}>
        <Grid container item xs={9}>
          <SearchBar
            id="search-groups"
            isLoading={isLoadingLicenseGroups}
            searchValue={searchValue}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
        </Grid>
        <Grid container item xs={3} justify="flex-end">
          <RefreshButton
            id="btn-refresh"
            onClick={() => getLicenseGroup()}
            animate={isLoadingLicenseGroups}
            disabled={isLoadingLicenseGroups}
          />
          <Permission value="create_license_group">
            <Grid item className={classes.addButton}>
              <Button
                startIcon={<AddIcon className={classes.addButtonIcon} />}
                keepButton
                id="create-group"
                onClick={() => handleCreateGroup()}
              >
                {i18n.t('CREATE_LICENSE_GROUP_KEY')}
              </Button>
            </Grid>
          </Permission>
        </Grid>
      </Grid>
      <Grid>
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  order={item?.order}
                  centerTitle={item?.centerTitle}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  markOderItem={itemOrder === item?.key}
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: LicenseGroupsFields) => (
            <TableRow key={item?.uuid}>
              <TableCell className={classes.rowAllRowsCount}>
                {item?.name}
              </TableCell>
              <TableCell className={classes.rowAllRowsCount}>
                {item?.license_limit}
              </TableCell>
              <TableCell className={classes.rowAllRowsCount}>
                {item?.clients_count}
              </TableCell>
              <TableCell
                textAlignCenter
                centerTitle
                className={classes.rowAllRowsCount}
              >
                {dateFormatWithAt(item?.created_at)}
              </TableCell>
              <TableCell
                textAlignCenter
                centerTitle
                className={classes.rowAllRowsCount}
              >
                {dateFormatWithAt(item?.updated_at)}
              </TableCell>
              <TableCell align="center">
                <MenuActions id={`btn-options-${item.uuid}`}>
                  <MenuActionItem
                    id="btn-group-details"
                    onClick={() => handleDetailsGroup(item?.uuid)}
                  >
                    {i18n.t('DETAILS_KEY')}
                  </MenuActionItem>
                  <Permission value="edit_license_group">
                    <MenuActionItem
                      id="btn-group-edit"
                      onClick={() => handleEditGroup(item)}
                    >
                      {i18n.t('EDIT_GROUP_INFO_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission value="edit_license_group_clients">
                    <MenuActionItem
                      id="btn-group-edit-clients"
                      onClick={() => handleEditClientsModal(item)}
                    >
                      {i18n.t('EDIT_CLIENTS_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission value="delete_license_group">
                    <MenuActionItem
                      id="btn-group-delete"
                      onClick={() => handleDeleteGroup(item?.uuid)}
                    >
                      {i18n.t('DELETE_KEY')}
                    </MenuActionItem>
                  </Permission>
                </MenuActions>
              </TableCell>
            </TableRow>
          )}
          data={listLicenseGroups}
          isLoading={!refresh && isLoadingLicenseGroups}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('GROUPS_PAGE.NO_GROUPS_KEY')}
        />
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  environments: { isLoadingLicenseGroups, listLicenseGroups },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  companySelected,
  role: data?.role,
  listLicenseGroups,
  isLoadingLicenseGroups,
});

export default connect(mapStateToProps, {
  getLicenseGroupsRequest,
  deleteLicenseGroupRequest,
  modalOpen,
  modalClose,
})(LicenseGroups);
